import type { PaginationData } from '@/utils/request';
import { request } from '@/utils/request';

/**
 * 获取首页店铺排行
 * 对应老项目 uni.$api.shop.getHomeRanking
 */
export function getHomeRanking(params: { limit: number }, siteCode: string) {
  return request<API.ShopRankItem[]>({
    url: '/newapi/goods/goodsIndex/getHomeRanking',
    method: 'POST',
    headers: {
      site: siteCode,
    },
    data: params,
  });
}

/**
 * 获取首页优质档口列表
 * 对应老项目 uni.$api.shop.getHotSellerList
 */
export function getHotSellers(params: { page: number, [key: string]: any }, siteCode: string) {
  return request({
    url: '/newapi/goods/goodsIndex/getHotSellers',
    method: 'POST',
    headers: {
      site: siteCode,
    },
    data: params,
  });
}

/**
 * 查询档口列表或排行榜
 * 对应老项目 uni.$api.shop.getSellerList
 */
export function getSellerList(data: any, siteCode: string) {
  return request<PaginationData<API.SellerItem[]>>({
    url: '/newapi/seller/seller/getSellerList',
    method: 'POST',
    headers: {
      site: siteCode,
    },
    data,
  });
}

/**
 * 获取随机档口列表（工厂列表：saleType 传 3）
 * 对应老项目 uni.$api.shop.getRandomSellerList，一次性返回 num 条随机数据（无分页）
 */
export function getRandomSellerList(data: { saleType: number, num: number }, siteCode: string) {
  return request<API.SellerItem[]>({
    url: '/newapi/seller/seller/getRandomSellerList',
    method: 'POST',
    headers: {
      site: siteCode,
    },
    data,
  });
}

/**
 * 获取随机工厂列表（工厂专区）
 * 一次性返回 num 条随机数据（无分页）
 */
export function getRandomFactoryList(data: { num: number }, siteCode: string) {
  return request<API.FactoryItem[]>({
    url: '/newapi/seller/seller/getRandomFactoryList',
    method: 'POST',
    headers: {
      site: siteCode,
    },
    data,
  });
}

/**
 * 获取市场列表（华强北市场专区）
 * POST /newapi/seller/market/getMarketList，免登录
 */
export function getMarketList(siteCode: string) {
  return request<API.MarketItem[]>({
    url: '/newapi/seller/market/getMarketList',
    method: 'POST',
    headers: {
      site: siteCode,
    },
    data: {},
  });
}

/**
 * 加载档口主营（分类）
 * 对应老项目 uni.$api.shop.loadShopTagDatas
 */
export function loadShopTagDatas(data: any, siteCode: string) {
  return request<API.SellerTagItem[]>({
    url: '/newapi/seller/seller/getSellerTagList',
    method: 'POST',
    headers: {
      site: siteCode,
    },
    data,
  });
}

/**
 * 获取所有档口的动态列表
 * 对应老项目 uni.$api.shop.getSellerTrendList
 */
export function getSellerTrendList(data: any, siteCode: string) {
  const url = data.sellerId
    ? '/newapi/seller/news/getNewsBySellerIdForOpen'
    : '/newapi/seller/news/getAllNewsForOpen';
  return request<PaginationData<API.SellerTrendItem[]>>({
    url,
    method: 'POST',
    headers: {
      site: siteCode,
    },
    data,
  });
}

/**
 * 点赞/取消点赞 档口动态
 * 对应老项目 uni.$api.shop.likeShopTrendItem
 */
export function likeShopTrendItem(data: any, siteCode: string) {
  return request<{ msg: string }>({
    url: '/newapi/seller/news/likeNews',
    method: 'POST',
    headers: {
      site: siteCode,
    },
    data,
  });
}

/**
 * 系统点赞档口动态
 * 对应老项目 uni.$api.shop.systemLikeShopTrendItem
 */
export function systemLikeShopTrendItem(data: any, siteCode: string) {
  return request<{ msg: string }>({
    url: '/newapi/seller/news/sysLikeNews',
    method: 'POST',
    headers: {
      site: siteCode,
    },
    data,
  });
}

/** 获取站点全部店铺列表 */
export function getAllShop(siteCode: string) {
  return request<any[]>({
    url: '/newapi/seller/seller/getAllSeller',
    method: 'post',
    data: {},
    headers: {
      site: siteCode,
    },
  });
}

/**
 * 加载档口详情（用于店铺详情页）
 * 对应老项目 uni.$api.shop.loadShopDetail
 */
export function loadShopDetail(sellersId: number | string, siteCode?: string) {
  return request<API.ShopDetailInfo>({
    url: '/newapi/seller/seller/getSellerInfo',
    method: 'POST',
    headers: { site: siteCode },
    data: {
      sellersId,
    },
  });
}

/**
 * 获取工厂详情（工厂档口：saleType=3 时级联请求）
 * 该接口需登录，未登录返回 code:-1；失败静默降级（notificationType: 'none'）
 */
export function getSellerFactoryInfo(sellersId: number | string, siteCode?: string) {
  return request<API.SellerFactoryInfo>({
    url: '/newapi/seller/seller/getSellerFactoryInfo',
    method: 'POST',
    headers: { site: siteCode },
    data: {
      sellersId,
    },
  }, {
    notificationType: 'none',
  });
}

/**
 * 收藏/取消收藏 档口
 * 对应老项目 uni.$api.shop.collectionShop
 * @param data { sellerIds?: number[], sellerId?: number }
 * @param isCollect 1=已收藏(取消收藏) 0=未收藏(收藏)
 */
export function collectionShop(data: any, isCollect: number) {
  const url = isCollect === 1
    ? '/newapi/seller/collections/delColSeller'
    : '/newapi/seller/collections/collectSeller';
  return request({
    url,
    method: 'POST',
    data,
  });
}

/**
 * 获取档口收藏状态
 * 对应老项目 uni.$api.shop.getCollectStatus
 */
export function getCollectStatus(data: { sellerIds: number[] }) {
  return request<Record<string, number>>({
    url: '/newapi/seller/collections/getCollectStatus',
    method: 'POST',
    data,
  });
}

/**
 * 加载档口分类（已迁移，之前已有实现但未命名导出）
 * 对应老项目 uni.$api.shop.loadShopCategory
 */
export function loadShopCategory(data: { sellersId: number | string }) {
  return request({
    url: '/newapi/seller/seller/getSellerCates',
    method: 'POST',
    data,
  });
}

/**
 * 加载商品列表V2（卖家店铺商品列表增强版）
 * 对应老项目 uni.$api.goods.loadGoodsListV2
 * 已在 src/api/goods/index.ts 中定义，此处为快捷引用
 */
export { loadGoodsListV2 } from '@/api/goods';
